import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  ogImage = '/og-image.png',
  ogType = 'website',
  canonicalUrl,
  noindex = false
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const params = useParams();
  const baseUrl = 'https://www.alzijdigi.nl';
  
  // Compute the full canonical URL
  const fullCanonicalUrl = useMemo(() => {
    if (typeof canonicalUrl === 'function') {
      return `${baseUrl}${canonicalUrl({ params })}`;
    }
    return canonicalUrl ? `${baseUrl}${canonicalUrl}` : baseUrl;
  }, [canonicalUrl, params]);

  // Compute the final title
  const finalTitle = useMemo(() => {
    if (typeof title === 'function') {
      return title({ params }) || defaultTitle;
    }
    return title || defaultTitle;
  }, [title, params]);

  // Compute the final description
  const finalDescription = useMemo(() => {
    if (typeof description === 'function') {
      return description({ params }) || defaultDescription;
    }
    return description || defaultDescription;
  }, [description, params]);
  
  // Default meta tags
  const defaultTitle = 'AlzijDigi | Custom Software & Digital Services in the Netherlands';
  const defaultDescription = 'AlzijDigi is a Dutch digital agency delivering tailored web development, mobile apps, SaaS, branding and marketing solutions.';
  const defaultKeywords = 'AlzijDigi, custom software Netherlands, web development, mobile apps, SaaS platforms, branding, SEO, digital marketing, Amersfoort';

  return (
    <Helmet key={location.pathname}>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <meta name="language" content={i18n.language} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={`${baseUrl}${ogImage}`} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="AlzijDigi" />
      <meta property="og:locale" content={i18n.language === 'nl' ? 'nl_NL' : 'en_NL'} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={`${baseUrl}${ogImage}`} />
      
      {/* Additional Meta Tags */}
      <meta name="author" content="AlzijDigi B.V." />
      <meta name="geo.region" content="NL" />
      <meta name="geo.placename" content="Amersfoort" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "AlzijDigi",
          "url": baseUrl,
          "logo": `${baseUrl}/logo192.png`,
          "description": defaultDescription,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Amersfoort",
            "addressCountry": "NL"
          },
          "sameAs": [
            "https://www.linkedin.com/company/alzijdigi",
            "https://twitter.com/alzijdigi"
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEO; 